/**
 * Building blocks shared by the COMMERCE screens: the API prefix, a loader
 * with an error state, single-flight submission with inline server field
 * errors, the cached commerce reference lists, a searchable multi-select and
 * a few labels.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Check, ChevronDown, RefreshCw, X } from 'lucide-react';
import clsx from 'clsx';
import { api, type ApiError } from '@/modules/accounting/lib/api';
import { reportError } from '@/modules/accounting/lib/store';
import { Badge, Button } from '@/modules/accounting/components/ui';
import { onDataChanged } from '@/lib/dataChanged';

export const CM = (path: string) => `/field/commerce${path}`;

/**
 * How money may arrive. Exactly the six the collections table accepts, in
 * the words a person uses, so no screen invents a seventh.
 */
export const COLLECTION_MODES = [
  { value: 'CASH', label: 'Cash' }, { value: 'CHEQUE', label: 'Cheque' }, { value: 'UPI', label: 'UPI' },
  { value: 'NEFT', label: 'NEFT' }, { value: 'RTGS', label: 'RTGS' }, { value: 'CARD', label: 'Card' },
];
export const COLLECTION_MODE_LABEL: Record<string, string> = Object.fromEntries(COLLECTION_MODES.map((m) => [m.value, m.label]));
export type Row = Record<string, any>;

export const human = (v: unknown) => (v === null || v === undefined || v === '' ? '—' : String(v).replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase()));

export function useFetch<T = any>(path: string | null, params?: Record<string, any>) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(!!path);
  const seq = useRef(0);
  const key = JSON.stringify(params ?? {});
  const reload = useCallback(async () => {
    if (!path) { setData(null); setLoading(false); return; }
    const mine = ++seq.current;
    setLoading(true);
    setError(null);
    try {
      const out = await api.get(path, params);
      if (mine === seq.current) setData(out);
    } catch (err) {
      if (mine === seq.current) setError(err as ApiError);
    } finally {
      if (mine === seq.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, key]);
  useEffect(() => { void reload(); }, [reload]);
  return { data, error, loading, reload, setData };
}

export function LoadError({ error, onRetry }: { error: ApiError; onRetry: () => void }) {
  return (
    <div className="card flex flex-col items-center justify-center text-center py-10 px-6">
      <AlertTriangle size={20} className="text-critical-500 mb-2" />
      <p className="text-sm font-medium">{error.status === 404 ? 'Not found' : 'This could not be loaded'}</p>
      <p className="text-[15px] muted mt-1 max-w-md">
        {error.status === 403 ? 'You do not have permission to see this.' : error.status === 404 ? 'It does not exist or is outside what you can see.' : error.message}
      </p>
      {error.status !== 404 && error.status !== 403 && <Button size="sm" className="mt-3" icon={RefreshCw} onClick={onRetry}>Try again</Button>}
    </div>
  );
}

export function fieldErrorsOf(err: ApiError | null | undefined): Record<string, string> {
  const d = err?.details;
  if (d?.fields && typeof d.fields === 'object') return { ...d.fields };
  return {};
}

/** One submission at a time; server field errors land in `errors`; the form stays open on failure. */
export function useSubmit() {
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const inFlight = useRef(false);
  const submit = useCallback(async <T,>(fn: () => Promise<T>, opts: { onError?: (err: ApiError) => boolean } = {}): Promise<T | undefined> => {
    if (inFlight.current) return undefined;
    inFlight.current = true;
    setBusy(true);
    setErrors({});
    try {
      return await fn();
    } catch (err) {
      const e = err as ApiError;
      setErrors(fieldErrorsOf(e));
      if (!opts.onError?.(e)) reportError(e);
      return undefined;
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }, []);
  return { busy, errors, setErrors, submit };
}

export interface Lookups {
  territories: Row[]; channels: Row[]; shop_types: Row[]; sub_shop_types: Row[]; tags: Row[]; distributors: Row[];
  distributor_channel_types: Row[]; distributor_segmentations: Row[]; categories: Row[]; baskets: Row[]; cohorts: Row[];
  mrps: number[]; segments: string[];
}

let lookupCache: Promise<Lookups> | null = null;
export function invalidateLookups() { lookupCache = null; }
onDataChanged(invalidateLookups);

export function useLookups() {
  const [data, setData] = useState<Lookups | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const load = useCallback((fresh = false) => {
    if (fresh || !lookupCache) lookupCache = api.get(CM('/lookups'));
    setError(null);
    lookupCache!.then(setData).catch((err: ApiError) => { lookupCache = null; setError(err); });
  }, []);
  useEffect(() => { load(); }, [load]);
  return { lookups: data, error, reload: () => load(true) };
}

export const STATUS_TONE: Record<string, string> = { UPCOMING: 'brand', EXPIRED: 'neutral', DISABLED: 'neutral',
  DRAFT: 'neutral', PENDING_APPROVAL: 'caution', ACTIVE: 'positive', REJECTED: 'critical', INACTIVE: 'neutral',
};

export function SchemeStatus({ status }: { status: string }) {
  return <Badge tone={STATUS_TONE[status] ?? 'neutral'}>{human(status)}</Badge>;
}

export interface Option { value: number | string; label: string; sub?: string }

/**
 * A multi-select with search. Options are static, or fetched by `search`
 * (the chosen ones keep their labels through `selectedLabels`).
 */
export function MultiSelect({
  value, onChange, options, search, placeholder = 'Select…', disabled, error, selectedLabels,
}: {
  value: Array<number | string>; onChange: (v: Array<number | string>) => void; options?: Option[];
  search?: (q: string) => Promise<Option[]>; placeholder?: string; disabled?: boolean; error?: boolean;
  selectedLabels?: Record<string, string>;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [remote, setRemote] = useState<Option[]>([]);
  const [known, setKnown] = useState<Record<string, string>>({});
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (!search || !open) return undefined;
    let live = true;
    const t = setTimeout(() => {
      search(q).then((rows) => { if (live) setRemote(rows); }).catch((err) => { if (live) reportError(err); });
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [search, q, open]);

  const list = useMemo(() => {
    const src = search ? remote : (options ?? []);
    const needle = q.trim().toLowerCase();
    return search || !needle ? src : src.filter((o) => `${o.label} ${o.sub ?? ''}`.toLowerCase().includes(needle));
  }, [search, remote, options, q]);

  const labelOf = (v: number | string) => options?.find((o) => String(o.value) === String(v))?.label ?? selectedLabels?.[String(v)] ?? known[String(v)] ?? String(v);
  const toggle = (o: Option) => {
    const has = value.some((v) => String(v) === String(o.value));
    setKnown((k) => ({ ...k, [String(o.value)]: o.label }));
    onChange(has ? value.filter((v) => String(v) !== String(o.value)) : [...value, o.value]);
  };

  return (
    <div className="relative" ref={box}>
      <button type="button" disabled={disabled} onClick={() => setOpen(!open)}
        className={clsx('input flex items-center gap-1 flex-wrap min-h-9 text-left pr-8', error && 'input-error', disabled && 'opacity-60 cursor-not-allowed')}>
        {value.length === 0 && <span className="muted">{placeholder}</span>}
        {value.slice(0, 6).map((v) => (
          <span key={String(v)} className="badge-brand inline-flex items-center gap-1">
            {labelOf(v)}
            {!disabled && <X size={11} className="cursor-pointer" onClick={(e) => { e.stopPropagation(); onChange(value.filter((x) => String(x) !== String(v))); }} />}
          </span>
        ))}
        {value.length > 6 && <span className="text-xs muted">+{value.length - 6} more</span>}
        <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none" />
      </button>
      {open && !disabled && (
        <div className="absolute z-40 mt-1 w-full min-w-[240px] rounded-lg border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900 shadow-pop">
          <div className="p-2 border-b border-ink-100 dark:border-ink-800">
            <input autoFocus className="input h-8" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <ul className="max-h-64 overflow-auto py-1">
            {list.length === 0 && <li className="px-3 py-2 text-[15px] muted">{search && !remote.length ? 'Type to search' : 'Nothing matches'}</li>}
            {list.map((o) => {
              const on = value.some((v) => String(v) === String(o.value));
              return (
                <li key={String(o.value)}>
                  <button type="button" onClick={() => toggle(o)} className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-[15px] hover:bg-ink-50 dark:hover:bg-ink-800">
                    <span className={clsx('w-4 h-4 rounded border flex items-center justify-center', on ? 'bg-brand-600 border-brand-600' : 'border-ink-300')}>
                      {on && <Check size={11} className="text-white" strokeWidth={3} />}
                    </span>
                    <span className="flex-1 min-w-0 truncate">{o.label}</span>
                    {o.sub && <span className="text-2xs muted">{o.sub}</span>}
                  </button>
                </li>
              );
            })}
          </ul>
          {value.length > 0 && (
            <div className="p-2 border-t border-ink-100 dark:border-ink-800 flex justify-end">
              <button type="button" className="btn-ghost btn-sm" onClick={() => onChange([])}>Clear all</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export const searchProducts = (categoryId?: number | string | null) => async (q: string): Promise<Option[]> => {
  const rows: Row[] = await api.get(CM('/lookups/products'), { search: q || undefined, category_id: categoryId || undefined, limit: 40 });
  return rows.map((p) => ({ value: Number(p.id), label: p.name, sub: p.code }));
};

export const searchOutlets = async (q: string): Promise<Option[]> => {
  const rows: Row[] = await api.get(CM('/lookups/outlets'), { search: q || undefined, limit: 40 });
  return rows.map((o) => ({ value: Number(o.id), label: o.name, sub: o.code }));
};

export const fmtDate = (v: unknown) => {
  if (!v) return '—';
  const s = String(v).slice(0, 10);
  const [y, m, d] = s.split('-');
  return y && m && d ? `${d}-${m}-${y}` : s;
};
